import { noOp } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import { DateString } from 'common/UrlDate';
import { Maybe } from 'graphql/jsutils/Maybe';
import { isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DishFirstFoodItemFragment,
  DishFirstFoodItemPreviewFragment,
  useGetFoodItemLazyQuery,
} from 'src/gqlReactTypings.generated.d';
import { dishFirstFoodItemFragmentToCartShef } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstModal/hooks/useFoodItemAvailability';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { useHandleAddToCartDateUpdates } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { useTracker } from 'src/shared/hooks/useTracker';
import Notifications from 'src/shared/Notifications';
import { useDispatch, useSelector } from 'src/store';
import { decLineItemQuantity, incLineItemQuantity, setShefs } from 'src/store/cart/actions';
import { CartActionLocation, CartFoodItem } from 'src/store/cart/types';
import { match, P } from 'ts-pattern';

export type FoodItemData =
  | { type: 'local'; foodItem: CartFoodItem }
  | { type: 'network'; foodItem: DishFirstFoodItemFragment };

interface UseQuickAddToCartParams {
  deliveryDate?: DateString;
  foodItemPreview: DishFirstFoodItemPreviewFragment;
  segmentMetrics: ISegmentMetrics | undefined;
  idx: number | undefined;
  location: CartActionLocation;
  zipCode: string;
  quantity: number | null | undefined;
  numAvailable: number | null | undefined;
}

interface UseQuickAddToCartResults {
  increment: () => void;
  decrement: () => void;
  loading: boolean;
  error: boolean;
  maxQuantity: number;
}

export const useQuickAddToCart = ({
  deliveryDate,
  location,
  foodItemPreview: { foodItemId, slug, shef },
  zipCode,
  segmentMetrics,
  idx,
  numAvailable,
}: UseQuickAddToCartParams): UseQuickAddToCartResults => {
  const tracker = useTracker();

  const dispatch = useDispatch();
  const cartFoodItemData = useSelector(({ cart }) => cart.foodItems.entities[foodItemId]);
  const { handleDeliveryDateUpdates } = useHandleAddToCartDateUpdates(deliveryDate);

  const [fetchFoodItem, { data, loading, error }] = useGetFoodItemLazyQuery();
  const foodItemVariables = useMemo(
    () => ({ variables: { shefId: shef.id, slug, zipCode } }),
    [shef.id, slug, zipCode]
  );
  // try to default to using what is in the cart if possible
  // shape of data in the cart and what is recieved from the network is different
  // so creating this return type lets us type card in code below
  const foodItemData: Maybe<FoodItemData> = useMemo(() => {
    type MatchInput = [Maybe<DishFirstFoodItemFragment>, Maybe<CartFoodItem>];
    return match<MatchInput, Maybe<FoodItemData>>([data?.sluggedFoodItem, cartFoodItemData])
      .with([P.not(P.nullish), P.any], ([foodItem]) => ({ type: 'network', foodItem }))
      .with([P.any, P.not(P.nullish)], ([, foodItem]) => ({ type: 'local', foodItem }))
      .otherwise(() => undefined);
  }, [data?.sluggedFoodItem, cartFoodItemData]);

  const updateFoodItemQuantity = useCallback(
    (modify: 'inc' | 'dec', foodItemData: FoodItemData) => {
      // TODO Check if we need to disable quick ATC for flexible date
      if (isUndefined(deliveryDate)) {
        return;
      }
      // set data needed to validate item when added for the first time to cart
      // if it's been added before, this context is already available to the cart
      if (foodItemData.type === 'network') {
        const formattedItem = { id: foodItemData.foodItem.foodItemId, ...foodItemData.foodItem };
        dispatch(setShefs([dishFirstFoodItemFragmentToCartShef(formattedItem)]));
      }

      const cartFoodItem =
        foodItemData.type === 'network'
          ? { id: foodItemData.foodItem.foodItemId, ...foodItemData.foodItem }
          : foodItemData.foodItem;
      const payload = { foodItem: cartFoodItem, deliveryDate, location, segmentMetrics, idx };
      dispatch(modify === 'inc' ? incLineItemQuantity(false, payload) : decLineItemQuantity(false, payload));
    },
    [deliveryDate, dispatch, location, segmentMetrics, idx]
  );

  const maxQuantity = useMemo(
    () =>
      !isUndefined(deliveryDate)
        ? match(foodItemData)
            .with(P.nullish, () => 1) // we can assume at least 1 available if item is present on explore results
            .with({ type: 'local' }, () => numAvailable ?? 0)
            .with(
              { type: 'network' },
              (foodItemData) =>
                foodItemData.foodItem.availability.find((availability) =>
                  availability.availabilityDate.startsWith(deliveryDate)
                )?.numAvailable ?? 0
            )
            .exhaustive()
        : 0,
    [numAvailable, deliveryDate, foodItemData]
  );

  const hasError = !!error;
  const [quickAddError, setQuickAddError] = useState(false);
  useEffect(() => {
    if (!hasError) return noOp;

    setQuickAddError(true);
    Notifications.error('An error occured while adding this item to cart. Try again later.');

    const timeout = setTimeout(() => setQuickAddError(false), 3000);
    return () => clearTimeout(timeout);
  }, [hasError]);

  // count number of times user tries to add but fails
  const [retryCount, setRetryCount] = useState(0);
  const modQuantity = useCallback(
    (mod: 'inc' | 'dec') => {
      if (foodItemData) return updateFoodItemQuantity(mod, foodItemData);
      const start = new Date();
      fetchFoodItem(foodItemVariables)
        .then(({ data }) => {
          data?.sluggedFoodItem && updateFoodItemQuantity(mod, { type: 'network', foodItem: data.sluggedFoodItem });
          tracker.track(ClientEvents.DISH_FIRST_LAZY_LOAD_QUICK_ADD_ITEM, {
            result: 'success',
            userRetryCount: retryCount,
            waitTimeMs: new Date().getTime() - start.getTime(),
          });
        })
        .catch((err) => {
          console.error(err);
          tracker.track(ClientEvents.DISH_FIRST_LAZY_LOAD_QUICK_ADD_ITEM, {
            result: 'error',
            userRetryCount: retryCount,
            waitTimeMs: new Date().getTime() - start.getTime(),
          });
          setRetryCount(retryCount + 1);
        });

      handleDeliveryDateUpdates();
    },
    [foodItemData, fetchFoodItem, foodItemVariables, updateFoodItemQuantity, maxQuantity, retryCount]
  );

  const increment = useCallback(() => modQuantity('inc'), [modQuantity]);
  const decrement = useCallback(() => modQuantity('dec'), [modQuantity]);

  return { increment, decrement, loading, error: quickAddError, maxQuantity };
};
